import React, { useEffect, useState } from 'react';
import { Modal, Icon, Button, Input, Form, Message } from 'semantic-ui-react';
import { TrackingInfoDto } from '../../types/tracking';
import { TrackingTimeline } from './TrackingTimeline';

export interface TrackingModalProps {
  open: boolean;
  /** Código já persistido no contrato (vazio = ainda não cadastrado na BD). */
  trackingCode: string;
  /** Obrigatório para gravar código via PUT. */
  contractId?: string | null;
  /** Quando true, exibe formulário de cadastro/edição (ex.: só Admin). */
  canEditCode?: boolean;
  trackingInfo: TrackingInfoDto | null;
  loading: boolean;
  fetchError?: string | null;
  saveLoading?: boolean;
  saveError?: string | null;
  /** Persiste o código e deve atualizar o estado no pai (ex.: novo trackingCode + GET timeline). */
  onSaveCode?: (code: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Modal com linha do tempo dos Correios e, para quem pode editar, cadastro/alteração do código.
 */
const TrackingModal: React.FC<TrackingModalProps> = ({
  open,
  trackingCode,
  contractId,
  canEditCode = false,
  trackingInfo,
  loading,
  fetchError,
  saveLoading = false,
  saveError,
  onSaveCode,
  onClose,
}) => {
  const persisted = (trackingCode || '').trim();
  const [localDraft, setLocalDraft] = useState('');
  /** true = a mostrar campo para novo código ou alteração */
  const [alterMode, setAlterMode] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalDraft(persisted);
      setAlterMode(!persisted);
    }
  }, [open, persisted]);

  const showForm = Boolean(canEditCode && contractId && (!persisted || alterMode));

  const handleSave = async () => {
    const trimmed = localDraft.trim();
    if (!trimmed || !onSaveCode) return;
    try {
      await onSaveCode(trimmed);
      setAlterMode(false);
    } catch {
      // Erro e mensagem tratados no componente pai (saveError)
    }
  };

  const headerSubtitle = persisted || localDraft.trim() ? persisted || localDraft.trim() : null;

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon closeOnDimmerClick={false}>
      <Modal.Header style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="truck" />
          Rastreamento Correios
        </div>
        {headerSubtitle ? (
          <div
            style={{
              fontSize: '0.85rem',
              fontWeight: 'normal',
              opacity: 0.85,
              wordBreak: 'break-all',
              maxWidth: '100%',
            }}
          >
            {headerSubtitle}
          </div>
        ) : null}
      </Modal.Header>
      <Modal.Content
        scrolling
        style={{
          paddingTop: '1.25rem',
          maxHeight: 'min(70vh, 560px)',
          overflowY: 'auto',
        }}
      >
        {showForm && (
          <Form
            style={{ marginBottom: '1.25rem' }}
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
          >
            <Form.Field>
              <label>Código de rastreio (Correios)</label>
              <Input
                placeholder="Ex.: BR123456789BR"
                value={localDraft}
                onChange={(_, d) => setLocalDraft(d.value)}
                disabled={saveLoading}
                style={{ maxWidth: '100%' }}
              />
            </Form.Field>
            {saveError ? <Message negative size="small" content={saveError} /> : null}
            <Button
              type="submit"
              primary
              disabled={saveLoading || !localDraft.trim()}
              loading={saveLoading}
            >
              <Icon name="save" />
              Salvar código
            </Button>
          </Form>
        )}

        {canEditCode && persisted && !alterMode && contractId && (
          <div style={{ marginBottom: '1rem' }}>
            <Button type="button" basic size="small" onClick={() => setAlterMode(true)}>
              <Icon name="edit" />
              Alterar código
            </Button>
          </div>
        )}

        {persisted ? (
          <TrackingTimeline
            trackingCode={persisted}
            loading={loading}
            info={trackingInfo}
            emptyMessage={!loading && fetchError ? fetchError : null}
          />
        ) : (
          !showForm &&
          !loading && (
            <Message info>
              Este contrato ainda não tem código de rastreio cadastrado.
              {canEditCode ? ' Utilize o formulário acima para salvar.' : ' Peça a um administrador para cadastrar.'}
            </Message>
          )
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} basic>
          <Icon name="close" />
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TrackingModal;
